import React, { useRef, useEffect, useState } from "react";
import { create as createPinchZoomPan } from "pinch-zoom-pan";
import "../../scss/familyTree/pinchZoomPan.scss";

/**
 * PinchZoomPanContainer Component
 * Wrapper for pinch-zoom-pan library to provide touch-friendly zoom/pan
 * Features:
 * - Mouse wheel zoom with proper limit handling
 * - Pinch zoom on touch devices
 * - Pan by dragging
 * - Prevents browser zoom on limit
 * - Smooth animations
 */
const PinchZoomPanContainer = ({
  children,
  minZoom = 0.5,
  maxZoom = 3,
  onZoomChange = null,
  initialZoom = 1,
  initialPan = { x: 0, y: 0 },
}) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const pzpRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(initialZoom);
  const [isAtMaxZoom, setIsAtMaxZoom] = useState(false);
  const [isAtMinZoom, setIsAtMinZoom] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    try {
      // Initialize pinch-zoom-pan with the root container
      pzpRef.current = createPinchZoomPan({
        element: containerRef.current,
        minZoom,
        maxZoom,
        captureWheel: true,
      });

      // Setup zoom state tracking and custom wheel handling
      let lastKnownZoom = initialZoom;

      // Prevent default browser zoom behavior and customize zoom control
      const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();

          const delta = e.deltaY > 0 ? -0.1 : 0.1;
          const newZoom = Math.max(
            minZoom,
            Math.min(maxZoom, lastKnownZoom + delta)
          );

          // Only allow zoom if not at limits
          if (
            (delta > 0 && newZoom < maxZoom) ||
            (delta < 0 && newZoom > minZoom)
          ) {
            pzpRef.current.update({ z: newZoom });
            lastKnownZoom = newZoom;
          }
        }
      };

      containerRef.current.addEventListener("wheel", handleWheel, {
        passive: false,
      });

      // Handle mouse down/up for cursor feedback
      const handleMouseDown = () => {
        setIsDragging(true);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
      };

      const handleMouseLeave = () => {
        setIsDragging(false);
      };

      containerRef.current.addEventListener("mousedown", handleMouseDown);
      containerRef.current.addEventListener("mouseup", handleMouseUp);
      containerRef.current.addEventListener("mouseleave", handleMouseLeave);

      // Monitor zoom changes via periodic polling
      const updateZoom = () => {
        if (pzpRef.current) {
          // pinch-zoom-pan doesn't expose zoom directly, so we track via updates
          setCurrentZoom(lastKnownZoom);
          setIsAtMaxZoom(Math.abs(lastKnownZoom - maxZoom) < 0.01);
          setIsAtMinZoom(Math.abs(lastKnownZoom - minZoom) < 0.01);

          if (onZoomChange) {
            onZoomChange(lastKnownZoom);
          }
        }
      };

      const zoomChangeInterval = setInterval(updateZoom, 100);

      return () => {
        clearInterval(zoomChangeInterval);
        const container = containerRef.current;
        container?.removeEventListener("wheel", handleWheel);
        container?.removeEventListener("mousedown", handleMouseDown);
        container?.removeEventListener("mouseup", handleMouseUp);
        container?.removeEventListener("mouseleave", handleMouseLeave);
        if (pzpRef.current) {
          try {
            pzpRef.current.destroy();
          } catch (e) {
            console.warn("Error destroying PinchZoomPan:", e);
          }
        }
      };
    } catch (error) {
      console.error("Error initializing PinchZoomPan:", error);
    }
  }, [minZoom, maxZoom, initialZoom, onZoomChange]);

  /**
   * Public methods to control zoom/pan from parent component
   */
  const handleZoomIn = () => {
    if (pzpRef.current && !isAtMaxZoom) {
      const newZoom = Math.min(maxZoom, currentZoom + 0.1);
      pzpRef.current.update({ z: newZoom });
    }
  };

  const handleZoomOut = () => {
    if (pzpRef.current && !isAtMinZoom) {
      const newZoom = Math.max(minZoom, currentZoom - 0.1);
      pzpRef.current.update({ z: newZoom });
    }
  };

  const handleReset = () => {
    if (pzpRef.current) {
      pzpRef.current.reset();
    }
  };

  const handleFitToView = () => {
    if (pzpRef.current) {
      // Fit to view - zoom out to minimum zoom level
      pzpRef.current.update({ z: minZoom });
    }
  };

  // Expose methods via ref
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.zoomIn = handleZoomIn;
      containerRef.current.zoomOut = handleZoomOut;
      containerRef.current.reset = handleReset;
      containerRef.current.fitToView = handleFitToView;
      containerRef.current.getZoom = () => currentZoom;
      containerRef.current.pzp = pzpRef.current;
    }
  }, [currentZoom, isAtMaxZoom, isAtMinZoom]);

  return (
    <div
      ref={containerRef}
      className={`pinch-zoom-pan-container ${isDragging ? "dragging" : ""}`}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        // position: "relative",

        flex: 1,
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <div
        ref={contentRef}
        className="pzp-content"
        style={{
          width: "100%",
          height: "100%",
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PinchZoomPanContainer;
